/**
 * Vehicle Processing presentation adapter for Operational Readiness.
 * Maps Operations metrics + latest provider event — no pipeline redesign.
 */

import {ProviderEvent, VehicleTransaction, VisionContext} from '../../../models/vte';
import {OperationsMetrics} from '../../../services/vte/operations-metrics.service';
import {resolveProviderEventStatusBadge, resolveTransactionStatusBadge,} from '../../../services/vte/vte.constants';
import {OperationalStatusCardView, OperationalWarningView,} from '../operational-readiness.models';

export type VehicleProcessingAdapterInput = {
    visionContext: VisionContext | null;
    metrics: OperationsMetrics | null;
    /** Outer null = load failed; inner event null = loaded, none yet. */
    latestProviderEvent: ProviderEvent | null | undefined;
    providerEventsLoadFailed: boolean;
};

function readyCard(
    key: string,
    title: string,
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'ready',
        statusLabel: 'Ready',
        detail,
    };
}

function informationalCard(
    key: string,
    title: string,
    detail: string,
    statusLabel = 'Info',
): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'informational',
        statusLabel,
        detail,
    };
}

function pendingCard(
    key: string,
    title: string,
    warning: OperationalWarningView,
    status: 'missing' | 'warning' | 'blocked' = 'missing',
    statusLabel = 'Pending',
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status,
        statusLabel,
        detail,
        warnings: [warning],
    };
}

function unknownCard(key: string, title: string, detail: string): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'unknown',
        statusLabel: 'Unknown',
        detail,
    };
}

function formatTimestamp(value: string | null | undefined): string {
    if (!value) {
        return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return parsed.toLocaleString();
}

function pipelineEnabled(visionContext: VisionContext | null): boolean | null {
    if (visionContext == null) {
        return null;
    }
    return visionContext.vte_pipeline_enabled !== false;
}

function latestTransactionDetail(transaction: VehicleTransaction): string {
    const status =
        resolveTransactionStatusBadge(transaction.status)?.label || transaction.status;
    const plate = transaction.correlated_plate || 'No plate';
    const anchor = formatTimestamp(transaction.anchor_timestamp);
    return `Latest: ${plate} · ${status} · ${anchor}`;
}

function latestEventDetail(event: ProviderEvent): string {
    const status =
        resolveProviderEventStatusBadge(event.processing_status)?.label
        || event.processing_status;
    return `Last event: ${formatTimestamp(event.received_at)} · Status: ${status}`;
}

/**
 * Exactly three operator-facing cards for Vehicle Processing readiness.
 */
export function mapVehicleProcessingCards(
    input: VehicleProcessingAdapterInput,
): OperationalStatusCardView[] {
    const {visionContext, metrics, latestProviderEvent, providerEventsLoadFailed} = input;
    const enabled = pipelineEnabled(visionContext);
    const cards: OperationalStatusCardView[] = [];

    // 1. Vehicle Event Ingestion
    if (providerEventsLoadFailed) {
        cards.push(
            unknownCard(
                'vehicle_event_ingestion',
                'Vehicle Event Ingestion',
                'Latest vehicle event evidence could not be loaded.',
            ),
        );
    } else if (latestProviderEvent) {
        cards.push(
            readyCard(
                'vehicle_event_ingestion',
                'Vehicle Event Ingestion',
                latestEventDetail(latestProviderEvent),
            ),
        );
    } else {
        cards.push(
            informationalCard(
                'vehicle_event_ingestion',
                'Vehicle Event Ingestion',
                'No vehicle events received for this organization yet.',
                'No activity',
            ),
        );
    }

    // 2. Vehicle Processing Pipeline
    if (enabled === false) {
        cards.push(
            pendingCard(
                'vehicle_processing_pipeline',
                'Vehicle Processing Pipeline',
                {
                    problem: 'Vehicle processing is disabled for this organization.',
                    impact: 'Incoming vehicle events will not be processed into vehicle activity.',
                    recommendedAction:
                        'Enable the vehicle processing pipeline for this organization when runtime processing is required.',
                    scope: 'organization',
                },
                'blocked',
                'Disabled',
            ),
        );
    } else if (metrics === null) {
        cards.push(
            unknownCard(
                'vehicle_processing_pipeline',
                'Vehicle Processing Pipeline',
                'Vehicle processing activity could not be loaded.',
            ),
        );
    } else if (!metrics.latestTransaction) {
        cards.push(
            informationalCard(
                'vehicle_processing_pipeline',
                'Vehicle Processing Pipeline',
                enabled
                    ? 'Processing is enabled. No processed vehicle activity yet.'
                    : 'No processed vehicle activity yet.',
                'No activity',
            ),
        );
    } else {
        const waiting = metrics.waiting;
        const waitingPart =
            waiting > 0 ? ` Waiting: ${waiting}.` : '';
        cards.push(
            readyCard(
                'vehicle_processing_pipeline',
                'Vehicle Processing Pipeline',
                `${latestTransactionDetail(metrics.latestTransaction)}.${waitingPart}`,
            ),
        );
    }

    // 3. Processing Attention
    if (enabled === false) {
        cards.push(
            informationalCard(
                'processing_attention',
                'Processing Attention',
                'Not applicable while vehicle processing is disabled.',
                'Not applicable',
            ),
        );
        return cards;
    }

    if (metrics === null) {
        cards.push(
            unknownCard(
                'processing_attention',
                'Processing Attention',
                'Processing attention metrics could not be loaded.',
            ),
        );
        return cards;
    }

    const needsAttention = metrics.needsAttention;
    const waiting = metrics.waiting;

    if (needsAttention > 0) {
        const waitingNote =
            waiting > 0 ? ` Waiting transactions: ${waiting}.` : '';
        cards.push(
            pendingCard(
                'processing_attention',
                'Processing Attention',
                {
                    problem: `${needsAttention} vehicle processing item(s) need attention.`,
                    impact:
                        'Some vehicle activity may be incomplete, timed out, or waiting for operator follow-up.',
                    recommendedAction:
                        'Open Operations → Vehicle Transactions filtered to Needs Attention and resolve outstanding items.',
                    scope: 'organization',
                },
                'warning',
                'Needs attention',
                `Needs attention: ${needsAttention}.${waitingNote}`,
            ),
        );
        return cards;
    }

    if (waiting > 0) {
        cards.push(
            readyCard(
                'processing_attention',
                'Processing Attention',
                `${waiting} transaction(s) waiting for required events. No items currently need attention.`,
            ),
        );
        return cards;
    }

    cards.push(
        readyCard(
            'processing_attention',
            'Processing Attention',
            'No processing attention required.',
        ),
    );
    return cards;
}
